import * as React from "react";
import AnalyticsChart, { ChartData } from "../AnalyticsChart";
import { AnalyticsSpinner } from "../shared";

/**
 * MonthlyBillingProgress Component
 * 
 * Displays monthly billing progress with visual indicators showing billed vs expected revenue
 */

interface MonthData {
  month: string;
  month_name: string;
  expected_monthly_revenue: number;
  actual_billed: number;
  remaining_expected: number;
  expected_so_far: number;
  billing_efficiency: number;
  days_elapsed: number;
  total_days: number;
  completion_percentage: number;
}

interface BillingComparison {
  revenue_change: number;
  efficiency_change: number;
  completion_change: number;
}

interface MonthlyBillingData {
  current_month: MonthData;
  previous_month: MonthData;
  comparison: BillingComparison;
}

interface MonthlyBillingProgressProps {
  isLoading?: boolean;
  error?: string | null;
  className?: string;
  height?: number;
  showComparison?: boolean;
  filters?: {
    month?: string;
    plan_filter?: string;
    utm_source_filter?: string;
  };
}

const MonthlyBillingProgress: React.FC<MonthlyBillingProgressProps> = ({
  isLoading = false,
  error = null,
  className = "",
  height = 400,
  showComparison = true,
  filters = {}
}) => {
  const [billingData, setBillingData] = React.useState<MonthlyBillingData | null>(null);
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [fetchError, setFetchError] = React.useState<string | null>(null);

  // Fetch billing progress data
  const fetchBillingData = React.useCallback(async () => {
    setIsLoadingData(true);
    setFetchError(null);
    try {
      const params = new URLSearchParams();
      
      if (filters.month) params.append('month', filters.month);
      if (filters.plan_filter) params.append('plan_filter', filters.plan_filter);
      if (filters.utm_source_filter) params.append('utm_source_filter', filters.utm_source_filter);

      const url = `/admin/analytics/subscriptions/monthly_billing.json?${params}`;

      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || ''
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setBillingData(data);
    } catch (error) {
      console.error('MonthlyBillingProgress - Error fetching billing data:', error);
      setBillingData(null);
      setFetchError('Unable to load billing progress. Please try again.');
    } finally {
      setIsLoadingData(false);
    }
  }, [filters.month, filters.plan_filter, filters.utm_source_filter]);

  // Fetch data on mount and when filters change
  React.useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    if (!Number.isFinite(amount)) return 'Not available';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value: number): string => {
    if (!Number.isFinite(value)) return 'Not available';
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // Get progress bar variant based on efficiency
  const getProgressVariant = (efficiency: number): string => {
    if (efficiency >= 100) return 'success';
    if (efficiency >= 80) return 'info';
    if (efficiency >= 60) return 'warning';
    return 'danger';
  };

  // Get comparison icon and color
  const getComparisonIcon = (change: number): { icon: string; color: string } => {
    if (change > 0) return { icon: 'fas fa-arrow-up', color: 'text-success' };
    if (change < 0) return { icon: 'fas fa-arrow-down', color: 'text-danger' };
    return { icon: 'fas fa-minus', color: 'text-muted' };
  };

  // Prepare chart data for progress visualization
  const getProgressChartData = (): ChartData => {
    if (!billingData) return { labels: [], datasets: [] };

    const currentMonth = billingData.current_month;
    
    return {
      labels: ['Progress'],
      datasets: [
        {
          label: 'Billed',
          data: [Number.isFinite(currentMonth.actual_billed) ? currentMonth.actual_billed : 0],
          backgroundColor: '#28a745',
          borderColor: '#28a745',
          borderWidth: 2
        },
        {
          label: 'Remaining',
          data: [Number.isFinite(currentMonth.remaining_expected) ? currentMonth.remaining_expected : 0],
          backgroundColor: '#dc3545',
          borderColor: '#dc3545',
          borderWidth: 2
        }
      ]
    };
  };

  if (error || fetchError) {
    return (
      <div className={`monthly-billing-progress ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-pie me-2" aria-hidden="true"></i>
              Monthly Billing Progress
            </h6>
          </div>
          <div className="card-body">
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2" aria-hidden="true"></i>
              <strong>Error:</strong> {error || fetchError}
              {!error && <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={fetchBillingData}>Retry</button>}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isLoadingData) {
    return (
      <div className={`monthly-billing-progress ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-pie me-2" aria-hidden="true"></i>
              Monthly Billing Progress
            </h6>
          </div>
          <div className="card-body">
            <AnalyticsSpinner
              height={height}
              message="Loading billing progress..."
            />
          </div>
        </div>
      </div>
    );
  }

  if (!billingData) {
    return (
      <div className={`monthly-billing-progress ${className}`.trim()}>
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">
              <i className="fas fa-chart-pie me-2" aria-hidden="true"></i>
              Monthly Billing Progress
            </h6>
          </div>
          <div className="card-body text-center py-5 text-muted">
            <i className="fas fa-chart-pie fa-2x mb-3" aria-hidden="true"></i>
            <p className="mb-0">No billing data available</p>
          </div>
        </div>
      </div>
    );
  }

  const currentMonth = billingData.current_month;
  const progressVariant = getProgressVariant(currentMonth.billing_efficiency);
  const progressPercentage = Number.isFinite(currentMonth.completion_percentage)
    ? Math.min(100, Math.max(0, currentMonth.completion_percentage)) : 0;
  const hasComparison = billingData.previous_month.expected_monthly_revenue > 0
    && Object.values(billingData.comparison).every(value => Number.isFinite(value));

  return (
    <div className={`monthly-billing-progress ${className}`.trim()}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h6 className="mb-1">
                <i className="fas fa-chart-pie me-2" aria-hidden="true"></i>
                Monthly Billing Progress
              </h6>
              <small className="text-muted">{currentMonth.month_name}</small>
            </div>
            <div className="text-end">
              <div className={`badge bg-${progressVariant} fs-6`}>
                {formatPercentage(currentMonth.billing_efficiency)}
              </div>
              <div className="text-muted small">efficiency</div>
            </div>
          </div>
        </div>

        <div className="card-body">
          {/* Progress Overview */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Monthly Progress</span>
                <span className="text-muted">
                  {currentMonth.days_elapsed} of {currentMonth.total_days} days
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="progress mb-3" style={{ height: '20px' }}>
                <div
                  className={`progress-bar bg-${progressVariant}`}
                  role="progressbar"
                  style={{ width: `${progressPercentage}%` }}
                  aria-label="Monthly billing progress"
                  aria-valuenow={progressPercentage}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  {formatPercentage(currentMonth.completion_percentage)}
                </div>
              </div>
            </div>
          </div>

          {/* Revenue Breakdown */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="text-center p-3 bg-light rounded">
                <div className="h4 mb-1 text-primary">
                  {formatCurrency(currentMonth.expected_monthly_revenue)}
                </div>
                <div className="small text-muted">Expected MRR</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center p-3 bg-light rounded">
                <div className="h4 mb-1 text-success">
                  {formatCurrency(currentMonth.actual_billed)}
                </div>
                <div className="small text-muted">Billed</div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="text-center p-3 bg-light rounded">
                <div className="h4 mb-1 text-warning">
                  {formatCurrency(currentMonth.remaining_expected)}
                </div>
                <div className="small text-muted">Remaining</div>
              </div>
            </div>
          </div>

          {/* Progress Chart */}
          <div className="row mb-4">
            <div className="col-12">
              <div style={{ height: '200px' }}>
                <AnalyticsChart
                  type="doughnut"
                  data={getProgressChartData()}
                  options={{
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                  height={200}
                  showLegend={true}
                />
              </div>
            </div>
          </div>

          {/* Comparison with Previous Month */}
          {showComparison && (
            <div className="row">
              <div className="col-12">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="card-title mb-3">
                      <i className="fas fa-chart-line me-2" aria-hidden="true"></i>
                      vs. {billingData.previous_month.month_name}
                    </h6>
                    
                    {!hasComparison ? (
                      <p className="text-muted mb-0">No comparison available for this period.</p>
                    ) : <div className="row text-center">
                      <div className="col-md-4">
                        <div className="d-flex align-items-center justify-content-center">
                          <i className={`${getComparisonIcon(billingData.comparison.revenue_change).icon} me-2 ${getComparisonIcon(billingData.comparison.revenue_change).color}`} aria-hidden="true"></i>
                          <div>
                            <div className="fw-bold">
                              {formatCurrency(Math.abs(billingData.comparison.revenue_change))}
                            </div>
                            <small className="text-muted">Revenue Change</small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="d-flex align-items-center justify-content-center">
                          <i className={`${getComparisonIcon(billingData.comparison.efficiency_change).icon} me-2 ${getComparisonIcon(billingData.comparison.efficiency_change).color}`} aria-hidden="true"></i>
                          <div>
                            <div className="fw-bold">
                              {formatPercentage(billingData.comparison.efficiency_change)}
                            </div>
                            <small className="text-muted">Efficiency Change</small>
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-4">
                        <div className="d-flex align-items-center justify-content-center">
                          <i className={`${getComparisonIcon(billingData.comparison.completion_change).icon} me-2 ${getComparisonIcon(billingData.comparison.completion_change).color}`} aria-hidden="true"></i>
                          <div>
                            <div className="fw-bold">
                              {formatPercentage(billingData.comparison.completion_change)}
                            </div>
                            <small className="text-muted">Completion Change</small>
                          </div>
                        </div>
                      </div>
                    </div>}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .monthly-billing-progress .progress-bar {
          font-size: 0.8rem;
          font-weight: 600;
        }

        .monthly-billing-progress .badge.fs-6 {
          font-size: 1rem !important;
        }

        .monthly-billing-progress .bg-light {
          background-color: rgba(var(--bs-gray-100-rgb), 0.5) !important;
        }

        @media (max-width: 768px) {
          .monthly-billing-progress .row .col-md-4 {
            margin-bottom: 1rem;
          }
          
          .monthly-billing-progress .card-header .d-flex {
            flex-direction: column;
            align-items: stretch;
            gap: 1rem;
          }
          
          .monthly-billing-progress .text-end {
            text-align: start !important;
          }
        }
      `}</style>
    </div>
  );
};

MonthlyBillingProgress.displayName = 'MonthlyBillingProgress';

export default MonthlyBillingProgress;
export type { MonthData, BillingComparison, MonthlyBillingData };
